import {
  ProductCapability,
  combineCapabilities,
  type BillingConfig,
  type DeploymentManifest,
  type OfferingAdminNav,
  type OfferingHttpRoute,
  type OfferingMarketingContent,
  type OfferingRoutes,
  type ProductAddon,
  type ProductAddonPlugin,
  type ProductManifest,
  type ProductOfferingPlugin,
  type ProductPricing,
  type PublicApiEndpoint,
} from "@constructorfleet/offerings-sdk";

export type UaOfferingCatalog = {
  id: string;
  name: string;
  stateCode: string;
  stateName: string;
  marketing: OfferingMarketingContent;
  billing: BillingConfig;
  manifest: ProductManifest;
  addons: ProductAddon[];
};

export type CreateUaOfferingCatalogInput = {
  id: string;
  name: string;
  stateCode: string;
  stateName: string;
  marketing: OfferingMarketingContent;
  pricing: ProductPricing;
  addons?: ProductAddon[];
  version?: string;
};

export type UaOfferingPlugin<
  TDbClient = unknown,
  TSettings = unknown,
  TSaveResult = unknown,
  TRendered = unknown,
> = ProductOfferingPlugin<TDbClient, TSettings, TSaveResult, TRendered> & {
  manifest: ProductManifest;
  billing: BillingConfig;
  deployment?: DeploymentManifest;
  stateCode: string;
  stateName: string;
};

export type CreateUaOfferingPluginInput<
  TDbClient = unknown,
  TSettings = unknown,
  TSaveResult = unknown,
  TRendered = unknown,
> = {
  catalog: UaOfferingCatalog;
  routes?: Partial<OfferingRoutes>;
  adminNav?: Partial<OfferingAdminNav>;
  httpRoutes?: OfferingHttpRoute[];
  addons?: ProductAddonPlugin<TDbClient, TRendered>[];
  deployment?: DeploymentManifest;
  publicApiEndpoints?: { basePath: string; endpoints: PublicApiEndpoint[] };
  loadSettings(dbClient: TDbClient): Promise<TSettings>;
  saveSettings(settings: TSettings, dbClient: TDbClient): Promise<TSaveResult>;
  adminUI(dbClient: TDbClient): TRendered;
  productPage(dbClient: TDbClient): TRendered;
  dashboardPage?(dbClient: TDbClient): TRendered;
  signupPage?(dbClient: TDbClient): TRendered;
  demoPage?(dbClient: TDbClient): TRendered;
};

type DerivedCapabilityFlags = {
  hasDashboard: boolean;
  hasRoutes: boolean;
  hasBackgroundJob: boolean;
  hasBilling: boolean;
  hasAddons: boolean;
};

function buildCapabilities(flags: DerivedCapabilityFlags) {
  const capabilities = [ProductCapability.AdminSettings];

  if (flags.hasDashboard) {
    capabilities.push(ProductCapability.Dashboard);
  }
  if (flags.hasRoutes) {
    capabilities.push(ProductCapability.Routes);
  }
  if (flags.hasBackgroundJob) {
    capabilities.push(ProductCapability.BackgroundJob);
  }
  if (flags.hasBilling) {
    capabilities.push(ProductCapability.Billing);
  }
  if (flags.hasAddons) {
    capabilities.push(ProductCapability.Addons);
  }

  return combineCapabilities(...capabilities);
}

function hasBackgroundWorkload(deployment?: DeploymentManifest): boolean {
  return (
    deployment?.topology?.workloads?.some(
      (workload) => workload.kind === "worker" || workload.kind === "job"
    ) ?? false
  );
}

function buildDefaultRoutes(
  productId: string,
  overrides: Partial<OfferingRoutes>,
  includeSignup: boolean,
  includeDemo: boolean
): OfferingRoutes {
  const routes: OfferingRoutes = {
    productPath: overrides.productPath ?? `products/${productId}`,
    adminSettingsPath: overrides.adminSettingsPath ?? `admin/apps/${productId}`,
  };

  if (includeSignup || overrides.signupPath) {
    routes.signupPath = overrides.signupPath ?? `products/${productId}/signup`;
  }

  if (includeDemo || overrides.demoPath) {
    routes.demoPath = overrides.demoPath ?? `products/${productId}/demo`;
  }

  return routes;
}

export function createUaOfferingCatalog(input: CreateUaOfferingCatalogInput): UaOfferingCatalog {
  const addons = [...(input.addons ?? [])];
  const billing: BillingConfig = {
    billingModel: input.pricing.billingModel,
    priceDimensions: input.pricing.priceDimensions,
    pricing: { ...input.pricing },
    ...(addons.length > 0 ? { addons } : {}),
  };

  return {
    id: input.id,
    name: input.name,
    stateCode: input.stateCode,
    stateName: input.stateName,
    marketing: { ...input.marketing },
    billing,
    manifest: {
      version: input.version ?? "1.0.0",
      capabilities: buildCapabilities({
        hasDashboard: false,
        hasRoutes: false,
        hasBackgroundJob: false,
        hasBilling: true,
        hasAddons: addons.length > 0,
      }),
      billingModel: input.pricing.billingModel,
      pricing: { ...input.pricing },
      ...(addons.length > 0 ? { addons } : {}),
    },
    addons,
  };
}

export function createUaOfferingPlugin<
  TDbClient = unknown,
  TSettings = unknown,
  TSaveResult = unknown,
  TRendered = unknown,
>(
  input: CreateUaOfferingPluginInput<TDbClient, TSettings, TSaveResult, TRendered>
): UaOfferingPlugin<TDbClient, TSettings, TSaveResult, TRendered> {
  const pluginAddons = [...(input.addons ?? [])];
  const manifestAddons = pluginAddons.length > 0 ? pluginAddons : input.catalog.addons;
  const manifest: ProductManifest = {
    ...input.catalog.manifest,
    capabilities: buildCapabilities({
      hasDashboard: Boolean(input.dashboardPage),
      hasRoutes: (input.httpRoutes?.length ?? 0) > 0,
      hasBackgroundJob: hasBackgroundWorkload(input.deployment),
      hasBilling: Boolean(input.catalog.billing.pricing),
      hasAddons: manifestAddons.length > 0,
    }),
    ...(manifestAddons.length > 0 ? { addons: manifestAddons } : {}),
  };

  return {
    id: input.catalog.id,
    name: input.catalog.name,
    stateCode: input.catalog.stateCode,
    stateName: input.catalog.stateName,
    marketing: { ...input.catalog.marketing },
    routes: buildDefaultRoutes(
      input.catalog.id,
      input.routes ?? {},
      Boolean(input.signupPage),
      Boolean(input.demoPage)
    ),
    httpRoutes: input.httpRoutes ? [...input.httpRoutes] : [],
    adminNav: {
      navLabel: input.adminNav?.navLabel ?? input.catalog.name,
      adminLinkLabel: input.adminNav?.adminLinkLabel ?? `Open ${input.catalog.name} settings`,
    },
    ...(pluginAddons.length > 0 ? { addons: pluginAddons } : {}),
    manifest,
    billing: {
      ...input.catalog.billing,
      ...(manifestAddons.length > 0 ? { addons: manifestAddons } : {}),
    },
    ...(input.deployment ? { deployment: input.deployment } : {}),
    ...(input.publicApiEndpoints ? { publicApiEndpoints: input.publicApiEndpoints } : {}),
    loadSettings: input.loadSettings,
    saveSettings: input.saveSettings,
    adminUI: input.adminUI,
    productPage: input.productPage,
    ...(input.dashboardPage ? { dashboardPage: input.dashboardPage } : {}),
    ...(input.signupPage ? { signupPage: input.signupPage } : {}),
    ...(input.demoPage ? { demoPage: input.demoPage } : {}),
  };
}
